/**
 * Treatments section of the consultation workspace.
 *
 * Self-contained — owns mutation hooks, inline form state, label printing,
 * and previous prescriptions. Signals mutation activity back to the parent
 * workspace via `setSectionBusy` so it can block end/lock/close actions.
 */
import { Loader2, PencilIcon, Plus, Printer, ScrollText, XIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { CatalogProduct, ConsultationType, ConsultationWithStock } from '@snootsvet/shared'
import { formatClinicPrice, getProductDisplayInfo } from '@snootsvet/shared'

import { DrawerOverlay } from '@/frontend/components/drawer-overlay'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { ConfirmButton } from '@/frontend/components/ui/confirm-button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/frontend/components/ui/tooltip'
import { useListCatalogProducts } from '@/frontend/features/inventory/use-list-catalog-products'
import { OrderStatusBadge, PaymentBadge } from '@/frontend/features/orders/order-badges'
import { TreatmentAttributes } from '@/frontend/features/patient/timeline/treatment-attributes'
import { TreatmentReminders } from '@/frontend/features/patient/treatment-reminders'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useGetPatient } from '@/frontend/hooks/api/patients/use-get-patient'
import { useDeleteTreatment } from '@/frontend/hooks/api/use-delete-treatment'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useUpdateTreatment } from '@/frontend/hooks/api/use-update-treatment'

import { useCreateTreatments } from './hooks/use-create-treatments'
import { usePrintOptimisticLabels } from './hooks/use-print-optimistic-labels'
import { PreviousTreatmentsList } from './previous-treatments-list'
import { TreatmentForm } from './treatment-form'
import type {
  ExternalFormItem,
  FormTreatmentItem,
  InternalFormItem,
  SavedTreatmentItem,
  TreatmentCategory,
} from './treatments.types'
import { toApiItem, toSavedItem } from './treatments.utils'

const CATEGORY_LABELS: Record<string, string> = {
  prescription: 'Prescription',
  external: 'External Script',
  service: 'Service',
  vaccination: 'Vaccine',
}

type InlineFormState =
  | { mode: 'closed' }
  | { mode: 'add'; defaults?: FormTreatmentItem }
  | { mode: 'edit'; treatmentId: string }

/** Type guard for items backed by clinic stock (internal variants). */
function isInternal<T extends FormTreatmentItem>(item: T): item is T & InternalFormItem {
  return item.treatmentCategory !== 'external'
}

/** Type guard for external prescription items. */
function isExternal<T extends FormTreatmentItem>(item: T): item is T & ExternalFormItem {
  return item.treatmentCategory === 'external'
}

function TreatmentRow({
  item,
  catalogProducts,
  disabled,
  locked,
  isSaving,
  onEdit,
  onRemove,
}: {
  item: SavedTreatmentItem
  catalogProducts?: CatalogProduct[]
  disabled: boolean
  locked: boolean
  isSaving: boolean
  onEdit: () => void
  onRemove: () => void
}) {
  const catalogProduct = isExternal(item)
    ? (catalogProducts?.find((p) => p.id === item.catalogProductId) ?? null)
    : null
  const product = isInternal(item) ? (item.product ?? null) : null
  const displayInfo =
    product || catalogProduct
      ? getProductDisplayInfo({ quantity: item.quantity, product, catalogProduct })
      : null

  const typeLabel = CATEGORY_LABELS[item.treatmentCategory] ?? item.treatmentCategory
  const isExternalItem = item.treatmentCategory === 'external'

  const price = item.totalPrice ?? null
  const discountBps = isInternal(item) && item.zeroFee ? 10_000 : 0

  return (
    <div className="flex items-center justify-between border-l-3 border-treatment py-2 pl-4">
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {isSaving && <Loader2 className="size-3 shrink-0 animate-spin text-muted-foreground" />}
          <span className="flex items-center gap-1.5">
            {isExternalItem && (
              <Tooltip>
                <TooltipTrigger
                  render={<ScrollText className="size-3 shrink-0 text-muted-foreground" />}
                />
                <TooltipContent>External prescription</TooltipContent>
              </Tooltip>
            )}
            <span className="text-sm text-muted-foreground">{typeLabel}</span>
            {' · '}
            <span className="font-medium">{displayInfo?.name ?? 'Unknown product'}</span>
            {price != null && isInternal(item) && (
              <Badge variant="outline" size="sm" className="tabular-nums">
                {discountBps > 0 && item.subtotalPrice != null && (
                  <s className="text-muted-foreground">
                    {formatClinicPrice(item.subtotalPrice, item.clinicId)}
                  </s>
                )}
                <span className={discountBps > 0 ? 'font-semibold text-foreground' : undefined}>
                  {formatClinicPrice(price, item.clinicId)}
                </span>
                {discountBps > 0 && (
                  <span className="text-muted-foreground">({discountBps / 100}% discount)</span>
                )}
              </Badge>
            )}
          </span>
          {item.orderStatus && <OrderStatusBadge status={item.orderStatus} size="sm" />}
          {item.orderPayment && <PaymentBadge order={item.orderPayment} size="sm" />}
        </div>
        <TreatmentAttributes
          className="mt-0.5"
          dispensedQuantity={
            item.type === 'internal_administered'
              ? null
              : (displayInfo?.quantityLabel ?? `${item.quantity} units`)
          }
          administeredQuantity={
            item.type === 'internal_administered'
              ? (displayInfo?.quantityLabel ?? `${item.quantity} units`)
              : null
          }
          batchNumbers={'allocations' in item ? item.allocations.map((a) => a.batchNumber) : null}
          repeatsAllowed={'repeatsAllowed' in item ? item.repeatsAllowed : null}
          refillInterval={'refillInterval' in item ? item.refillInterval : null}
          instructions={'instructions' in item ? item.instructions : null}
          tagNumber={'tagNumber' in item ? item.tagNumber : null}
          injectionSite={'injectionSite' in item ? item.injectionSite : null}
        />
      </div>
      {!disabled && (
        <div className="flex shrink-0 ml-2">
          <Button
            variant="ghost"
            size="icon-sm"
            type="button"
            disabled={locked || isSaving}
            onClick={onEdit}
          >
            <PencilIcon className="size-3.5" />
          </Button>
          <ConfirmButton
            variant="ghost"
            size="icon-sm"
            type="button"
            disabled={locked || isSaving}
            className="text-destructive-fg"
            heading="Remove treatment?"
            description="This will remove the treatment from the list."
            confirmLabel="Remove"
            onConfirm={onRemove}
          >
            <XIcon className="size-3.5" />
          </ConfirmButton>
        </div>
      )}
    </div>
  )
}

export function ConsultationWorkspaceTreatments({
  consultation,
  consultationType,
  setSectionBusy,
  readOnly,
}: {
  consultation: ConsultationWithStock
  consultationType: ConsultationType
  setSectionBusy: (key: string, busy: boolean) => void
  readOnly?: boolean
}) {
  // Species drives which booster reminder targets (diseases) are offered when
  // adding a vaccine — dogs/cats get a curated list, everything else the full union.
  const { data: patient } = useGetPatient(consultation.patientId, (r) => r.patient)

  const { region } = useViewerRegion()
  const { mutateAsync: createTreatment } = useCreateTreatments()
  const { mutateAsync: updateTreatment } = useUpdateTreatment()
  const { mutateAsync: deleteTreatmentMut } = useDeleteTreatment({
    patientId: consultation.patientId,
    consultationId: consultation.id,
  })

  const [mutatingIds, setMutatingIds] = useState(new Set<string>())
  const [creatingCount, setCreatingCount] = useState(0)
  const isMutating = mutatingIds.size > 0 || creatingCount > 0

  useEffect(() => {
    setSectionBusy('treatments', isMutating)
  }, [isMutating, setSectionBusy])

  const items: SavedTreatmentItem[] = useMemo(
    () => (consultation.treatments ?? []).map(toSavedItem),
    [consultation.treatments]
  )

  const currentProductIds = useMemo(
    () => new Set(items.flatMap((i) => ('productId' in i && i.productId ? [i.productId] : []))),
    [items]
  )
  const currentCatalogProductIds = useMemo(
    () =>
      new Set(
        items.flatMap((i) =>
          'catalogProductId' in i && i.catalogProductId ? [i.catalogProductId] : []
        )
      ),
    [items]
  )

  const addItem = useCallback(
    async (item: FormTreatmentItem) => {
      setCreatingCount((c) => c + 1)
      try {
        await createTreatment({
          patientId: consultation.patientId,
          consultationId: consultation.id,
          items: [toApiItem(item, region)],
        })
      } finally {
        setCreatingCount((c) => c - 1)
      }
    },
    [consultation.patientId, consultation.id, createTreatment, region]
  )

  const updateItem = useCallback(
    async (treatmentId: string, formItem: FormTreatmentItem) => {
      setMutatingIds((prev) => new Set(prev).add(treatmentId))
      try {
        await updateTreatment({
          id: treatmentId,
          patientId: consultation.patientId,
          consultationId: consultation.id,
          body: toApiItem(formItem, region),
        })
      } finally {
        setMutatingIds((prev) => {
          const next = new Set(prev)
          next.delete(treatmentId)
          return next
        })
      }
    },
    [consultation.patientId, consultation.id, updateTreatment, region]
  )

  const removeItem = useCallback(
    async (treatmentId: string) => {
      setMutatingIds((prev) => new Set(prev).add(treatmentId))
      try {
        await deleteTreatmentMut(treatmentId)
      } finally {
        setMutatingIds((prev) => {
          const next = new Set(prev)
          next.delete(treatmentId)
          return next
        })
      }
    },
    [deleteTreatmentMut]
  )

  // -- Inline form state ----------------------------------------------------

  const { data: staff } = useGetAuthenticatedStaff()
  const isRemoteVet = staff?.role === 'remote_vet'
  const { data: catalogProducts } = useListCatalogProducts()

  const [formState, setFormState] = useState<InlineFormState>({ mode: 'closed' })
  const [category, setCategory] = useState<TreatmentCategory>('prescription')
  const [isAdding, setIsAdding] = useState(false)

  const isFormOpen = formState.mode !== 'closed'
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [items]
  )

  // Looks up the raw server treatment (which carries `reminders`) by id, so vaccine
  // rows can render their booster reminders — `SavedTreatmentItem` doesn't carry them.
  const rawTreatmentById = useMemo(
    () => new Map((consultation.treatments ?? []).map((t) => [t.id, t])),
    [consultation.treatments]
  )

  const prescribingContext =
    consultationType === 'clinic'
      ? ('clinic_consultation' as const)
      : ('remote_consultation' as const)
  const clinicId = consultation.clinicId ?? undefined

  const handleAdd = () => {
    setCategory('prescription')
    setFormState({ mode: 'add' })
  }

  const handleEdit = (treatmentId: string) => {
    const item = sortedItems.find((i) => i.id === treatmentId)
    if (item) setCategory(item.treatmentCategory)
    setFormState({ mode: 'edit', treatmentId })
  }

  const handleCloseForm = () => {
    setFormState({ mode: 'closed' })
  }

  // -- Label printing -------------------------------------------------------

  const { printLabels, canPrint, hasPrintableTreatments, isPrinting } =
    usePrintOptimisticLabels(consultation)

  // -- Save handlers --------------------------------------------------------

  const handleAddSave = async (item: FormTreatmentItem) => {
    setIsAdding(true)
    try {
      await addItem(item)
      scrollTreatmentsIntoView()
      handleCloseForm()
    } catch {
      // Toast handled by hook
    } finally {
      setIsAdding(false)
    }
  }

  const handleEditSave = async (treatmentId: string, item: FormTreatmentItem) => {
    try {
      await updateItem(treatmentId, item)
      scrollTreatmentsIntoView()
      handleCloseForm()
    } catch {
      // Toast handled by hook
    }
  }

  // -- Render ---------------------------------------------------------------

  // Used to re-centre the treatments section after the inline form closes on
  // save — otherwise the container shrinks beneath the user and leaves them
  // stranded scrolled below the consultation.
  const treatmentsRef = useRef<HTMLDivElement>(null)
  function scrollTreatmentsIntoView() {
    treatmentsRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }

  // Only one inline form is ever open at a time (add or edit), so derive it once
  // and drop it into the matching position below.
  const editingItem =
    formState.mode === 'edit' ? sortedItems.find((i) => i.id === formState.treatmentId) : undefined
  const addDefaults = formState.mode === 'add' ? formState.defaults : undefined

  // If the item being edited disappears (e.g. a treatments refetch drops it), the
  // edit form would stop rendering while formState stays 'edit' — leaving the
  // section locked with no visible form to cancel. Close it to recover.
  useEffect(() => {
    if (formState.mode === 'edit' && !editingItem) setFormState({ mode: 'closed' })
  }, [formState, editingItem])
  const inlineForm = isFormOpen && (
    <div className="border-l-3 border-treatment p-4 flex flex-col gap-3">
      <TreatmentForm
        patientId={consultation.patientId}
        category={category}
        onCategoryChange={setCategory}
        prescribingContext={prescribingContext}
        contextClinicId={clinicId}
        patientSpecies={patient?.species ?? ''}
        defaultCreateTask={isRemoteVet}
        onCancel={handleCloseForm}
        editing={formState.mode === 'edit'}
        defaults={editingItem ?? addDefaults}
        saving={formState.mode === 'edit' ? mutatingIds.has(editingItem?.id ?? '') : isAdding}
        onSave={(item) =>
          editingItem ? handleEditSave(editingItem.id, item) : handleAddSave(item)
        }
      />
    </div>
  )

  return (
    <div ref={treatmentsRef} className="rounded-lg border bg-card">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <span className="font-semibold">Treatments</span>
        {hasPrintableTreatments && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            title="Print Labels"
            disabled={!canPrint || isPrinting}
            onClick={printLabels}
          >
            <Printer className="size-3.5" />
          </Button>
        )}
      </div>

      <DrawerOverlay className="p-4">
        <div className="flex flex-col gap-3">
          {sortedItems.map((item) => {
            const isPersistedReadonly = !item.editable
            const disabled = readOnly || isPersistedReadonly
            const isSaving = mutatingIds.has(item.id)

            if (formState.mode === 'edit' && formState.treatmentId === item.id) {
              return <div key={item.id}>{inlineForm}</div>
            }

            const raw = rawTreatmentById.get(item.id)

            return (
              <div key={item.id} className="flex flex-col gap-1">
                <TreatmentRow
                  item={item}
                  catalogProducts={catalogProducts}
                  disabled={disabled}
                  locked={isFormOpen}
                  isSaving={isSaving}
                  onEdit={() => handleEdit(item.id)}
                  onRemove={() => removeItem(item.id)}
                />
                {raw && (
                  <TreatmentReminders
                    treatment={raw}
                    patientId={consultation.patientId}
                    patientSpecies={patient?.species ?? ''}
                    consultationId={consultation.id}
                  />
                )}
              </div>
            )
          })}

          {formState.mode === 'add' && inlineForm}

          {!readOnly && !isFormOpen && (
            <Button type="button" variant="outline" onClick={handleAdd}>
              <Plus className="size-3.5 mr-1.5" />
              Add Treatment
            </Button>
          )}

          {!readOnly && (
            <PreviousTreatmentsList
              patientId={consultation.patientId}
              prescribingContext={prescribingContext}
              excludeProductIds={currentProductIds}
              excludeCatalogProductIds={currentCatalogProductIds}
              disabled={isFormOpen}
              onSelect={(item) => {
                setCategory(item.treatmentCategory)
                setFormState({ mode: 'add', defaults: item })
              }}
            />
          )}
        </div>
      </DrawerOverlay>
    </div>
  )
}
